<template>
  <div class="gw-origins">
    <label class="rmr-table-shell__filter-label">Origines</label>

    <!-- v-menu, not an absolutely positioned panel: `.rmr-table-shell` clips its
         overflow, and v-menu also gives us outside-click and Esc for free. -->
    <v-menu
      v-model="page.menuOpen"
      offset-y
      nudge-bottom="6"
      min-width="300"
      max-width="380"
      :close-on-content-click="false"
      content-class="gw-origins__menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="gw-origins__trigger"
          :class="{ 'gw-origins__trigger--open': page.menuOpen }"
          v-bind="attrs"
          v-on="on"
        >
          <span class="gw-origins__summary">{{ summary }}</span>
          <v-icon size="18" color="#94A3B8">{{ icons.mdiChevronDown }}</v-icon>
        </button>
      </template>

      <div class="gw-origins__panel">
        <div class="gw-origins__search">
          <rmr-text-field
            v-model="page.search"
            placeholder="Chercher un média source…"
            :prepend-inner-icon="icons.mdiMagnify"
            hide-details
            dense
          />
        </div>

        <div class="gw-origins__list">
          <label
            v-for="option in filteredOptions"
            :key="option.value"
            class="gw-origins__option"
          >
            <input
              type="checkbox"
              class="gw-origins__checkbox"
              :checked="isSelected(option.value)"
              @change="toggle(option.value)"
            />
            <span class="gw-origins__option-label" :title="option.label">
              {{ option.label }}
            </span>
            <rmr-soft-badge
              v-if="option.sourceName"
              size="xs"
              :label="option.presentation.label"
              :color="option.presentation.color"
              :background="option.presentation.background"
            />
          </label>

          <div v-if="filteredOptions.length === 0" class="gw-origins__empty">
            {{ page.loading ? "Chargement…" : "Aucun média trouvé" }}
          </div>
        </div>

        <div class="gw-origins__footer">
          <span class="gw-origins__count">
            {{ selectedCount === 0 ? "Aucun filtre" : `${selectedCount} sélectionnée(s)` }}
          </span>
          <rmr-action-button
            label="Tout effacer"
            variant="ghost"
            size="xs"
            :disabled="selectedCount === 0"
            @click="clearAll"
          />
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { computed, reactive, watch } from "@vue/composition-api";
import { mdiChevronDown, mdiMagnify } from "@mdi/js";
import debounce from "lodash/debounce";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { getSourcePresentation, prettifyOrigin } from "../source-presentation";

export default {
  name: "OriginMultiSelect",
  components: {
    RmrActionButton,
    RmrSoftBadge,
    RmrTextField,
  },
  props: {
    siteId: {
      type: Number,
      default: 0,
    },
    sourceIds: {
      type: Array,
      default: () => [],
    },
    dates: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { axiosGet } = useAxios();
    const { store } = useProxy();

    const page = reactive({
      loading: false,
      menuOpen: false,
      search: "",
      originList: [],
    });

    // =>  COMPUTED
    const selected = computed({
      get: () => store.getters["newsRadar/getOrigins"] || [],
      set: (value) => store.dispatch("newsRadar/updateOrigins", value),
    });

    const mediaType = computed(
      () => store.getters["newsRadar/getMediaType"] || "written"
    );

    const selectedCount = computed(() => selected.value.length);

    // news_radar_config carries the human label for a raw `from_entry`.
    const configByEntry = computed(() => {
      const configs = store.getters["newsRadar/getConfigList"] || [];
      return configs.reduce((acc, config) => {
        if (config.entry) {
          acc[config.entry] = config;
        }
        return acc;
      }, {});
    });

    const options = computed(() =>
      page.originList.map((entry) => {
        const config = configByEntry.value[entry];
        const sourceName = config?.news_radar_source?.name || "";

        return {
          value: entry,
          label: config?.name || prettifyOrigin(entry),
          sourceName,
          presentation: getSourcePresentation(sourceName),
        };
      })
    );

    const filteredOptions = computed(() => {
      const needle = page.search.trim().toLowerCase();
      if (!needle) return options.value;

      return options.value.filter((option) =>
        option.label.toLowerCase().includes(needle)
      );
    });

    // An empty `origins` means "no filter" server-side, so it reads as "all".
    const summary = computed(() => {
      if (selectedCount.value === 0) {
        return `Toutes les origines (${options.value.length})`;
      }
      if (selectedCount.value === 1) {
        const match = options.value.find(
          (option) => option.value === selected.value[0]
        );
        return match ? match.label : "1 origine sélectionnée";
      }
      return `${selectedCount.value} origines sélectionnées`;
    });

    // =>  METHODS
    const isSelected = (value) => selected.value.includes(value);

    const toggle = (value) => {
      selected.value = isSelected(value)
        ? selected.value.filter((entry) => entry !== value)
        : [...selected.value, value];
    };

    const clearAll = () => {
      selected.value = [];
    };

    const fetchOrigins = async () => {
      page.loading = true;
      try {
        const { data } = await axiosGet("/news-radar-data/origins", {
          ...(props.sourceIds.length > 0
            ? { source_ids: props.sourceIds.join(",") }
            : {}),
          ...(props.siteId > 0 ? { site_id: props.siteId } : {}),
          ...(props.dates.length === 2
            ? { start_date: props.dates[0], end_date: props.dates[1] }
            : {}),
          // Sinon la vue vidéo proposerait des comptes suivis en écrit, et
          // filtrer dessus ne rendrait rien.
          media_type: mediaType.value,
        });
        page.originList = Array.isArray(data) ? data : [];
        emit("count-changed", page.originList.length);

        // Drop selections that no longer exist in the new scope, otherwise the
        // request keeps filtering on entries the user can't see or untick.
        const stillAvailable = selected.value.filter((entry) =>
          page.originList.includes(entry)
        );
        if (stillAvailable.length !== selected.value.length) {
          selected.value = stillAvailable;
        }
      } catch (error) {
        console.error("error while fetching origins", error);
        page.originList = [];
        emit("count-changed", 0);
      }
      page.loading = false;
    };

    // Les configs donnent le libellé lisible d'une `from_entry`. On ne charge
    // que celles du support courant : une même entrée suivie en écrit ET en
    // vidéo a deux configs, donc deux noms, et la carte doit afficher celui de
    // la vue où l'on est.
    const fetchConfigs = async () => {
      if (!props.siteId) return;
      try {
        await store.dispatch("newsRadar/getConfigList", {
          siteId: props.siteId,
          mediaType: mediaType.value,
        });
      } catch (error) {
        console.error("error while fetching news radar configs", error);
      }
    };

    const refresh = debounce(async () => {
      await Promise.all([fetchOrigins(), fetchConfigs()]);
    }, 400);

    // =>  WATCH
    watch(() => props.siteId, refresh, { immediate: true });
    watch(() => props.sourceIds, refresh);
    watch(() => props.dates, refresh);
    watch(mediaType, refresh);

    // =>  DATA
    return {
      icons: { mdiChevronDown, mdiMagnify },
      clearAll,
      filteredOptions,
      isSelected,
      page,
      selectedCount,
      summary,
      toggle,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../_global-watch.scss";

.gw-origins {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.gw-origins__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: $gw-control-height;
  padding: 0 10px 0 12px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-0;
  color: $rmr-text-primary;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: $rmr-text-faint;
  }
}

.gw-origins__trigger--open {
  border-color: $rmr-indigo;
  box-shadow: 0 0 0 1px rgba($rmr-indigo, 0.14);
}

.gw-origins__summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style lang="scss">
// Unscoped: v-menu detaches its content outside this component's DOM subtree.
.gw-origins__menu {
  border-radius: $rmr-radius-lg !important;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.14) !important;
}

.gw-origins__panel {
  background: $rmr-surface-0;
  overflow: hidden;
}

.gw-origins__search {
  padding: 10px 10px 8px;
  border-bottom: 1px solid $rmr-border;
}

.gw-origins__list {
  max-height: 216px;
  overflow-y: auto;
  padding: 6px;
  @include rmr-scrollbar(8px);
}

.gw-origins__option {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 8px;
  border-radius: $rmr-radius-sm;
  font-size: 12.5px;
  cursor: pointer;

  &:hover {
    background: $rmr-surface-2;
  }
}

.gw-origins__checkbox {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  accent-color: $rmr-indigo;
  cursor: pointer;
}

.gw-origins__option-label {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: $rmr-text-base;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gw-origins__empty {
  padding: 14px 10px;
  font-size: 12px;
  color: $rmr-text-subtle;
  text-align: center;
}

.gw-origins__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid $rmr-border;
}

.gw-origins__count {
  font-size: 12px;
  font-weight: 600;
  color: $rmr-text-subtle;
}
</style>
